// Which tenant the browser is talking to, and what it has switched on.
//
// The build is shared by every tenant, so the product name cannot be baked in
// at compile time — it is fetched from GET /api/tenant, which the server
// answers from its own config.
//
// The fallback name is deliberately brand-free. Showing "XBG Backlog" for the
// half-second before the fetch resolves would flash the wrong company's name
// at an EHP user, which is precisely the thing these two deployments are
// supposed to never do.
//
// A tenant identity is a name and a logo, and nothing else the UI paints with:
// the `tenants.brand_color` / `brand_color_accent` columns (migration 0048)
// still exist and the server still reports them, but nothing here reads them.
// Every workspace wears the product's own palette, so a switch changes who the
// chrome names — not what colour it is.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { supabase, setCookieDomain, type SwitchableTenant } from "@/integrations/db/client";

/** Flags the server may report. Mirrors FEATURE_FLAGS in api/lib/featureFlags.ts. */
export type FeatureFlag = "ai-rewrite" | "notifications";

export interface Tenant {
  id: string;
  name: string;
  /** Web path to the tenant's logo asset, or null (renders the name alone). */
  logo: string | null;
  /** The tenant's original/bundled logo, before any upload — never changed by
   *  an upload, so `logo !== default_logo` is how the Company settings page
   *  knows there's an upload to revert. Only populated for the signed-in
   *  session's active tenant (from GET /api/auth/tenants); null in the
   *  unauthenticated host-fallback case, which never needs it. */
  default_logo: string | null;
  flags: FeatureFlag[];
  /** Support/contact address for the Login page's "Request an invite"
   *  mailto, or null if the superadmin hasn't set one (hides the link). */
  contact_email: string | null;
  /** The tenant's name for the ticket page's AI rewrite button, or null for
   *  DEFAULT_AI_REWRITE_LABEL (#9401). Read it through aiRewriteLabel() in lib/aiRewrite. */
  ai_rewrite_label: string | null;
}

/** Shown until /api/tenant answers, and if it never does. Names no tenant. */
const NEUTRAL: Tenant = {
  id: "",
  name: "Backlog",
  logo: null,
  default_logo: null,
  flags: [],
  contact_email: null,
  ai_rewrite_label: null,
};

interface HostTenant extends Tenant {
  /** The parent domain the session cookie is scoped to, or null (host-only). */
  cookie_domain: string | null;
  /** The default tenant — the apex / any unmapped host, shown as a public
   *  landing page rather than the auth-gated app (Phase 10). */
  is_default: boolean;
}

/** What the router needs to decide the landing branch; isDefault is null while
 *  the host tenant is still loading (show neither surface yet). */
export interface HostInfo {
  name: string;
  /** Web path to the host tenant's logo asset, or null. */
  logo: string | null;
  isDefault: boolean | null;
}

interface TenantContextValue {
  /** The tenant the user is acting in: the session's tenant when signed in
   *  (which may differ from the host after an in-place switch), else the host. */
  tenant: Tenant;
  /** The HOST tenant (from the request Host): brands the public landing and
   *  decides whether the apex shows it, independent of any session. */
  host: HostInfo;
  /** Tenants the user may switch into — empty unless signed in with more than one. */
  tenants: SwitchableTenant[];
  /** Parent domain for the session cookie / redirect eligibility, or null. */
  cookieDomain: string | null;
  /** Whether the signed-in account may manage tenant records (Settings →
   *  Tenants): null while a signed-in user's tenant list is still loading,
   *  false when signed out. A UI hint only — /api/platform/* re-checks it on
   *  every call. */
  isPlatformAdmin: boolean | null;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: NEUTRAL,
  host: { name: NEUTRAL.name, logo: null, isDefault: null },
  tenants: [],
  cookieDomain: null,
  isPlatformAdmin: false,
});

async function fetchHostTenant(): Promise<HostTenant> {
  const res = await fetch("/api/tenant");
  if (!res.ok) throw new Error(`GET /api/tenant returned ${res.status}`);
  const body = (await res.json()) as Partial<HostTenant>;
  return {
    id: typeof body.id === "string" ? body.id : "",
    name: typeof body.name === "string" && body.name.trim() ? body.name : NEUTRAL.name,
    logo: typeof body.logo === "string" && body.logo ? body.logo : null,
    // GET /api/tenant (unauthenticated) doesn't expose this — only the
    // signed-in membership list does, which is all the Company page needs.
    default_logo: null,
    flags: Array.isArray(body.flags) ? (body.flags as FeatureFlag[]) : [],
    contact_email: typeof body.contact_email === "string" && body.contact_email ? body.contact_email : null,
    ai_rewrite_label: typeof body.ai_rewrite_label === "string" && body.ai_rewrite_label ? body.ai_rewrite_label : null,
    cookie_domain: typeof body.cookie_domain === "string" && body.cookie_domain ? body.cookie_domain : null,
    is_default: body.is_default === true,
  };
}

export function TenantProvider({ children }: { children: ReactNode }) {
  // The host tenant brands the login screen and is the fallback identity. It
  // changes only on redeploy or a rename, so fetch it once.
  const { data: host } = useQuery({
    queryKey: ["tenant"],
    queryFn: fetchHostTenant,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  // Tell the client where to scope its session cookie, so a switch can redirect
  // across subdomains and still be signed in.
  useEffect(() => {
    setCookieDomain(host?.cookie_domain ?? null);
  }, [host?.cookie_domain]);

  // Track the signed-in USER ID (not just a boolean): the memberships query is
  // per-user, so it must be keyed by user id. Keyed only by ["auth","tenants"],
  // logging in as a second user right after a first reuses the first user's
  // still-fresh cache (staleTime + the app's refetchOnMount: false), so their
  // tenant set — e.g. whether the switcher shows — is wrong until a hard
  // refresh. The id in the key gives each user their own cache entry.
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, s) => setUserId(s?.user?.id ?? null));
    return () => data.subscription.unsubscribe();
  }, []);
  const signedIn = userId !== null;

  const { data: memberships } = useQuery({
    queryKey: ["auth", "tenants", userId],
    queryFn: async () => (await supabase.auth.listTenants()).data,
    enabled: signedIn,
    staleTime: 5 * 60 * 1000,
  });
  const tenants = memberships?.tenants ?? [];

  // Active identity follows the SESSION's tenant when signed in — after an
  // in-place switch the host still names the old tenant, so the `current`
  // membership (name AND flags) is the source of truth. Falls back to the host.
  const hostTenant: Tenant = host
    ? {
        id: host.id,
        name: host.name,
        logo: host.logo,
        default_logo: null,
        flags: host.flags,
        contact_email: host.contact_email,
        ai_rewrite_label: host.ai_rewrite_label,
      }
    : NEUTRAL;
  const current = tenants.find((t) => t.current);
  const active: Tenant =
    signedIn && current
      ? {
          id: current.slug,
          name: current.name,
          logo: current.logo,
          default_logo: current.default_logo,
          flags: current.flags as FeatureFlag[],
          contact_email: current.contact_email,
          ai_rewrite_label: current.ai_rewrite_label,
        }
      : hostTenant;

  // The landing decision is about the HOST, not the active session — a signed-in
  // user visiting the apex still gets the public landing. null until resolved.
  const hostInfo: HostInfo = {
    name: host?.name ?? NEUTRAL.name,
    logo: host?.logo ?? null,
    isDefault: host ? host.is_default : null,
  };

  return (
    <TenantContext.Provider
      value={{
        tenant: active,
        host: hostInfo,
        tenants,
        cookieDomain: host?.cookie_domain ?? null,
        // null, not false, until the list arrives: Settings must not send a
        // platform admin who is only a member here home before it knows.
        isPlatformAdmin: signedIn ? (memberships ? memberships.isPlatformAdmin : null) : false,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

/** The tenant the user is acting in. Never null — falls back to a brand-free default. */
export function useTenant(): Tenant {
  return useContext(TenantContext).tenant;
}

/** The HOST tenant — brands the public landing and gates the apex on it.
 *  `isDefault` is null while the host tenant is still loading. */
export function useHostTenant(): HostInfo {
  return useContext(TenantContext).host;
}

/** The tenants the signed-in user may switch into (drives the switcher UI). */
export function useSwitchableTenants(): SwitchableTenant[] {
  return useContext(TenantContext).tenants;
}

/** Whether the signed-in account is a platform admin — decides whether the
 *  nav offers Admin settings and Settings shows the Tenants tab. null while it
 *  is still loading, false when signed out. */
export function useIsPlatformAdmin(): boolean | null {
  return useContext(TenantContext).isPlatformAdmin;
}

/** The parent domain the session cookie is scoped to, or null (host-only).
 *  Paired with hostInDomain() it answers "can a session reach that host?" —
 *  which is what decides whether sending someone to another workspace's
 *  address lands them signed in or signed out. */
export function useCookieDomain(): string | null {
  return useContext(TenantContext).cookieDomain;
}

/** Whether a workspace can be reached from here with the session cookie: it
 *  needs a host, and that host has to sit under the domain the cookie is
 *  scoped to. Without this, "go to your workspace" can hand someone a page
 *  that asks them to sign in again — which is the whole thing this flow is
 *  meant to spare them. */
export function tenantReachable(primaryHost: string | null, cookieDomain: string | null): boolean {
  if (!primaryHost) return false;
  if (!cookieDomain) return false;
  return hostInDomain(primaryHost, cookieDomain);
}

/**
 * Whether a feature is on for the active tenant.
 *
 * Off while the tenant is still loading, so a gated control appears once we
 * know it should rather than flickering away once we learn it should not.
 */
export function useFeature(flag: FeatureFlag): boolean {
  return useContext(TenantContext).tenant.flags.includes(flag);
}

/** True when host is `domain` or a subdomain of it (cookie-reachable).
 *  Callers outside this module want tenantReachable(), which asks the whole
 *  question — one definition of "the session cookie reaches there" rather than
 *  two that can drift apart. */
function hostInDomain(host: string, domain: string): boolean {
  const h = host.split(":")[0].toLowerCase();
  const d = domain.toLowerCase();
  return h === d || h.endsWith(`.${d}`);
}

/**
 * Switch the signed-in user into another tenant they belong to.
 *
 * Re-mints the session, then either redirects to the tenant's own subdomain
 * (when the session cookie is scoped to a shared parent that reaches it) or
 * switches in place (dev, or an off-domain host): resetting tenant-scoped state
 * and landing on a safe root, since a deep link may not exist in the new tenant.
 */
export function useSwitchTenant() {
  const { cookieDomain } = useContext(TenantContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (target: SwitchableTenant) => {
      const { error } = await supabase.auth.switchTenant(target.id);
      if (error) throw new Error(error.message);
      return target;
    },
    onSuccess: (target) => {
      const currentHost = typeof window !== "undefined" ? window.location.host.split(":")[0].toLowerCase() : "";
      const canRedirect =
        !!cookieDomain &&
        !!target.primary_host &&
        hostInDomain(target.primary_host, cookieDomain) &&
        target.primary_host.toLowerCase() !== currentHost;

      if (canRedirect) {
        // The new session is in the parent-domain cookie; the target subdomain
        // reads it on load. A full navigation — nothing else to reset here.
        window.location.assign(`https://${target.primary_host}/`);
        return;
      }

      // In-place: the host still names the old tenant. Drop the workspace
      // selection (ids differ per tenant), invalidate every cached row, and land
      // on a safe root.
      sessionStorage.removeItem("selected-subsheet-id");
      queryClient.invalidateQueries();
      navigate("/");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Could not switch workspace");
    },
  });
}

/**
 * Star a workspace as this person's default, or clear it (pass null).
 *
 * Changes nothing about the session in flight — it only decides where signing
 * in at the shared "Welcome" host will land them next time. The membership
 * list is what renders the star, so the mutation invalidates that query rather
 * than keeping a second copy of the answer.
 */
export function useSetPreferredTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string | null) => {
      const { error } = await supabase.auth.setPreferredTenant(tenantId);
      if (error) throw new Error(error.message);
      return tenantId;
    },
    onSuccess: (tenantId) => {
      void queryClient.invalidateQueries({ queryKey: ["auth", "tenants"] });
      toast.success(tenantId ? "Default workspace set" : "Default workspace cleared");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Could not set your default workspace");
    },
  });
}
